import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import SlotHistoryDialog from "@/components/SlotHistoryDialog";
import { MapTrifold, Plus, Minus, ArrowsClockwise } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { alignPointsToOutline } from "@/lib/alignSlots";

const VB_W = 1000;
const VB_H = 620;
const PAD = 48;
const R = 17;

const FREE = "#10B981";
const OCC = "#334155";

export default function PlotView() {
  const navigate = useNavigate();
  const { data: config } = useQuery({ queryKey: ["config"], queryFn: async () => (await api.get("/config")).data });
  const { data: slots = [] } = useQuery({
    queryKey: ["slots"],
    queryFn: async () => (await api.get("/slots")).data,
    refetchInterval: 6000,
  });
  const [selected, setSelected] = useState(null);

  const { project, outline, points } = useMemo(() => {
    const layout = config?.layout || {};
    const pts = layout.points && typeof layout.points === "object" ? layout.points : {};
    const out = Array.isArray(layout.outline) ? layout.outline : [];
    const coords = [...out, ...Object.values(pts)];
    if (coords.length === 0) return { project: null, outline: out, points: pts };
    const lats = coords.map((c) => c[0]);
    const lngs = coords.map((c) => c[1]);
    const minLat = Math.min(...lats), maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
    const latMid = (minLat + maxLat) / 2;
    const kx = Math.cos((latMid * Math.PI) / 180) || 1;
    const dataW = Math.max((maxLng - minLng) * kx, 1e-9);
    const dataH = Math.max(maxLat - minLat, 1e-9);
    const scale = Math.min((VB_W - 2 * PAD) / dataW, (VB_H - 2 * PAD) / dataH);
    const offX = (VB_W - dataW * scale) / 2;
    const offY = (VB_H - dataH * scale) / 2;
    const project = ([lat, lng]) => [
      offX + (lng - minLng) * kx * scale,
      offY + (maxLat - lat) * scale,
    ];
    return { project, outline: out, points: pts };
  }, [config]);

  const bySlotNumber = {};
  slots.forEach((s) => { bySlotNumber[s.number] = s; });
  const hasLayout = project && Object.keys(points).length > 0;

  const [scale, setScale] = useState(1);
  const autoR = useMemo(() => {
    if (!project) return R;
    const pts = Object.values(points).map((ll) => project(ll));
    if (pts.length < 2) return R;
    let min = Infinity;
    for (let i = 0; i < pts.length; i++) {
      for (let j = i + 1; j < pts.length; j++) {
        const d = Math.hypot(pts[i][0] - pts[j][0], pts[i][1] - pts[j][1]);
        if (d < min) min = d;
      }
    }
    if (!isFinite(min)) return R;
    return Math.max(6, Math.min(R, min * 0.42));
  }, [project, points]);
  const r = autoR * scale;
  const fs = Math.max(6, r * 0.9);
  const occCount = slots.filter((s) => s.occupied).length;

  const svgRef = useRef(null);
  const panRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const CX = VB_W / 2, CY = VB_H / 2;
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const qc = useQueryClient();
  const alignedOnce = useRef(false);
  useEffect(() => {
    if (alignedOnce.current || !outline?.length || Object.keys(points).length < 2) return;
    const aligned = alignPointsToOutline(points, outline);
    if (!aligned) return;
    const changed = Object.keys(aligned).some((k) => {
      const a = aligned[k], b = points[k];
      return !b || Math.abs(a[0] - b[0]) > 1e-9 || Math.abs(a[1] - b[1]) > 1e-9;
    });
    if (!changed) { alignedOnce.current = true; return; }
    alignedOnce.current = true;
    const layout = config?.layout || {};
    api.put("/config/layout", { outline, center: layout.center || null, zoom: layout.zoom || null, points: aligned })
      .then(() => qc.invalidateQueries({ queryKey: ["config"] }))
      .catch(() => {});
  }, [outline, points, config, qc]);
  const nameLabels = useMemo(() => {
    if (!project) return [];
    const bySlot = {};
    slots.forEach((s) => { bySlot[s.number] = s; });
    const entries = Object.entries(points).map(([num, ll]) => ({ num, p: project(ll), s: bySlot[num] }));
    const blocked = [];
    entries.forEach(({ num, p, s }) => {
      const [cx, cy] = p;
      blocked.push({ x1: cx - r, y1: cy - r, x2: cx + r, y2: cy + r });
      if (s?.label) {
        const w = (String(num).length + 1) * fs * 0.5 + fs * 0.4;
        blocked.push({ x1: cx - w / 2, y1: cy + r + fs * 0.15, x2: cx + w / 2, y2: cy + r + fs * 1.15 });
      }
    });
    const eps = 0.75;
    const hits = (box, list) => list.some((b) => box.x2 - b.x1 > eps && b.x2 - box.x1 > eps && box.y2 - b.y1 > eps && b.y2 - box.y1 > eps);
    const placed = [];
    const out = [];
    entries
      .filter((e) => e.s?.occupied && e.s?.boat?.name)
      .forEach(({ p, s, num }) => {
        const [cx, cy] = p;
        const text = String(s.boat.name);
        const fz = fs * 0.8;
        const w = text.length * fz * 0.58 + fz * 0.7;
        const h = fz * 1.35;
        const above = cy - r - h * 0.32;
        const below = cy + r + h * 0.82;
        const boxAt = (yy) => ({ x1: cx - w / 2, y1: yy - h * 0.82, x2: cx + w / 2, y2: yy + h * 0.32 });
        let y = above;
        if (hits(boxAt(above), blocked) || hits(boxAt(above), placed)) {
          if (!hits(boxAt(below), blocked) && !hits(boxAt(below), placed)) y = below;
        }
        placed.push(boxAt(y));
        out.push({ key: num, x: cx, y, text, fz, w, h });
      });
    return out;
  }, [project, points, slots, r, fs]);

  const onDown = (e) => { panRef.current = { x: e.clientX, y: e.clientY, px: pan.x, py: pan.y }; };
  const onMove = (e) => {
    if (!panRef.current || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const k = VB_W / rect.width;
    setPan({ x: panRef.current.px + (e.clientX - panRef.current.x) * k, y: panRef.current.py + (e.clientY - panRef.current.y) * k });
  };
  const onUp = () => { panRef.current = null; };

  return (
    <div>
      <PageHeader
        title="Προβολή κάτοψης θέσεων"
        subtitle="Γραφική απεικόνιση της κατοχής των θέσεων"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate("/plot")} className="rounded-sm" data-testid="plotview-edit-btn">
              <MapTrifold size={18} weight="bold" /> Σχεδίαση κάτοψης θέσεων
            </Button>
          </div>
        }
      />
      <div className="space-y-4 p-6">
        <div className="rounded-sm border border-border bg-white px-4 py-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="font-head text-base font-bold text-[#234E7D]">{config?.name || "—"}</div>
              <div className="text-xs text-muted-foreground">
                {[config?.address, config?.city, config?.postal_code].filter(Boolean).join(", ") || "—"}
                {config?.phone ? ` · Τηλ. ${config.phone}` : ""}
              </div>
            </div>
            <div className="text-xs text-muted-foreground">Ημερομηνία: <span className="font-semibold text-foreground tabular-nums">{new Date().toLocaleDateString("el-GR")}</span> · <span className="font-semibold text-foreground tabular-nums">{occCount}/{slots.length}</span> κατειλημμένες</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-5 text-xs text-muted-foreground">
            <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: FREE }} /> Διαθέσιμη</div>
            <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: OCC }} /> Κατειλημμένη</div>
            <span>Κλικ σε αριθμό για στοιχεία. Σύρετε για μετακίνηση. Η κλίμακα αλλάζει μόνο από τα κουμπιά.</span>
          </div>
          {hasLayout && (
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                Κλίμακα θέσεων
                <input
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.05"
                  value={scale}
                  onChange={(e) => setScale(Number(e.target.value))}
                  className="h-1.5 w-28 cursor-pointer accent-[#0055FF]"
                  data-testid="plotview-scale-slider"
                />
                <span className="w-9 tabular-nums font-semibold text-foreground">{Math.round(scale * 100)}%</span>
              </label>
              <div className="flex items-center gap-1.5">
              <Button size="icon" variant="outline" className="h-8 w-8 rounded-sm" onClick={() => setZoom((z) => Math.min(6, z * 1.25))} data-testid="plotview-zoom-in"><Plus size={16} weight="bold" /></Button>
              <Button size="icon" variant="outline" className="h-8 w-8 rounded-sm" onClick={() => setZoom((z) => Math.max(0.5, z / 1.25))} data-testid="plotview-zoom-out"><Minus size={16} weight="bold" /></Button>
              <Button size="sm" variant="outline" className="rounded-sm" onClick={() => { resetView(); setScale(1); }} data-testid="plotview-zoom-reset"><ArrowsClockwise size={14} weight="bold" /> Επαναφορά</Button>
              </div>
            </div>
          )}
        </div>

        {!hasLayout ? (
          <div className="rounded-sm border border-dashed border-border p-10 text-center" data-testid="plotview-empty">
            <div className="text-sm text-muted-foreground">Δεν έχει σχεδιαστεί ακόμη η κάτοψη των θέσεων.</div>
            <Button className="mt-4 rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" onClick={() => navigate("/plot")} data-testid="plotview-goto-editor">
              <MapTrifold size={18} weight="bold" /> Σχεδίαση κάτοψης θέσεων
            </Button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-sm border border-border bg-white">
            <svg
              ref={svgRef}
              viewBox={`0 0 ${VB_W} ${VB_H}`}
              preserveAspectRatio="xMidYMid meet"
              style={{ width: "100%", aspectRatio: `${VB_W} / ${VB_H}`, display: "block", cursor: "grab", touchAction: "none" }}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerLeave={onUp}
              data-testid="plotview-canvas"
            >
              <defs>
                <pattern id="pv-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                  <path d="M40 0H0V40" fill="none" stroke="#E2E8F0" strokeWidth="1" />
                </pattern>
                <linearGradient id="pv-plot" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity="0.22" />
                  <stop offset="100%" stopColor="#059669" stopOpacity="0.10" />
                </linearGradient>
                <filter id="pv-shadow" x="-40%" y="-40%" width="180%" height="180%">
                  <feDropShadow dx="0" dy="1.5" stdDeviation="1.8" floodColor="#0F172A" floodOpacity="0.28" />
                </filter>
              </defs>
              <rect x="0" y="0" width={VB_W} height={VB_H} fill="#FBFDFF" />
              <rect x="0" y="0" width={VB_W} height={VB_H} fill="url(#pv-grid)" />
              <g transform={`translate(${pan.x} ${pan.y}) translate(${CX} ${CY}) scale(${zoom}) translate(${-CX} ${-CY})`}>
              {outline.length >= 3 && (
                <polygon
                  points={outline.map((p) => project(p).join(",")).join(" ")}
                  fill="url(#pv-plot)"
                  stroke="#059669"
                  strokeWidth="3"
                  strokeLinejoin="round"
                />
              )}
              {Object.entries(points).map(([num, ll]) => {
                const [cx, cy] = project(ll);
                const s = bySlotNumber[num];
                const occ = s?.occupied;
                return (
                  <g
                    key={num}
                    style={{ cursor: "pointer" }}
                    onClick={() => s && setSelected(s)}
                    data-testid={`plotview-slot-${num}`}
                  >
                    <circle cx={cx} cy={cy} r={r} fill={occ ? OCC : FREE} stroke="#fff" strokeWidth={Math.max(1, r * 0.16)} filter="url(#pv-shadow)" />
                    <text x={cx} y={cy + fs * 0.35} textAnchor="middle" fontSize={fs} fontWeight="700" fill="#fff" style={{ pointerEvents: "none" }}>{s?.label || num}</text>
                    {s?.label && (
                      <text x={cx} y={cy + r + fs * 0.95} textAnchor="middle" fontSize={fs * 0.75} fontWeight="600" fill="#64748B" stroke="#FBFDFF" strokeWidth={fs * 0.45} paintOrder="stroke" strokeLinejoin="round" style={{ pointerEvents: "none" }}>#{num}</text>
                    )}
                  </g>
                );
              })}
              {nameLabels.map((l) => (
                <g key={`bn-${l.key}`} style={{ pointerEvents: "none" }}>
                  <rect
                    x={l.x - l.w / 2}
                    y={l.y - l.h * 0.82}
                    width={l.w}
                    height={l.h}
                    rx={l.h * 0.28}
                    fill="#FFFFFF"
                    fillOpacity="0.94"
                    stroke="#C7D6E8"
                    strokeWidth={Math.max(0.6, l.fz * 0.07)}
                  />
                  <text x={l.x} y={l.y} textAnchor="middle" fontSize={l.fz} fontWeight="700" fill="#234E7D">{l.text}</text>
                </g>
              ))}
              </g>
            </svg>
          </div>
        )}
      </div>

      <SlotHistoryDialog
        slot={selected}
        open={!!selected}
        onOpenChange={(o) => !o && setSelected(null)}
      />
    </div>
  );
}
